import React, { useEffect, useId, useMemo, useRef, useState } from "react";
import {
  Avatar,
  Box,
  Button,
  Group,
  Modal,
  Select,
  Stack,
  Text,
  TextInput,
  Textarea,
  UnstyledButton,
} from "@mantine/core";
import { IconAlertTriangle } from "@tabler/icons-react";
import type {
  CalendarEventType,
  TimeBlockEvent,
} from "../../../../types/calendar";
import { useSalon } from "../../../../context/SalonContext";
import { filterBookableActiveStaff } from "../../../../utils/staffUtils";
import { DESIGN_ICONS, timeBlockIcon } from "../../../../theme/designIcons";
import CalendarDrawer from "../drawer/CalendarDrawer";
import { getInitials } from "../components/Views/sharedCalendarUtils";
import RepeatScreen from "./RepeatScreen";
import { ruleText, seriesText } from "./repeatRule";
import {
  SERIES_SCOPES,
  TIME_BLOCK_TYPES,
  TimeBlockErrors,
  TimeBlockFormState,
  addMinutesToTime,
  buildTimeBlockPayload,
  draftTimeBlockEvent,
  initialTimeBlockForm,
  isSeries,
  timeBlockConflicts,
  validateTimeBlock,
} from "./timeBlockModel";
import styles from "./TimeBlockDrawer.module.css";

const pad = (n: number) => String(n).padStart(2, "0");
const TIME_OPTIONS = Array.from({ length: 96 }, (_, i) => {
  const h = Math.floor(i / 4);
  const m = (i % 4) * 15;
  return {
    value: `${pad(h)}:${pad(m)}`,
    label: `${h % 12 || 12}:${pad(m)} ${h < 12 ? "AM" : "PM"}`,
  };
});

export interface TimeBlockDrawerProps {
  opened: boolean;
  onClose: () => void;
  /** The block being edited, or a seed (start time + staff) for a new one. */
  initialData:
    | TimeBlockEvent
    | { startTime?: string; resourceId?: string; date?: Date }
    | null;
  isEdit: boolean;
  isLoading: boolean;
  onSubmit: (payload: Record<string, unknown>) => void;
  onDelete: (appliesTo: "single" | "hereafter" | "all") => void;
  timezone: string | undefined;
  viewedDay: Date;
  /** Loaded calendar events: the conflicts list. */
  events: CalendarEventType[];
  /** Drawer slot nested screen ("repeat"). */
  nested: string | null;
  onOpenNested: (nested: "repeat") => void;
  onCloseNested: () => void;
  /** A new block's grid preview as the form changes (null when closed). */
  onDraftChange?: (draft: TimeBlockEvent | null) => void;
}

/**
 * The time block drawer (design handoff §7), drawer slot kind "timeBlock":
 * staff, type tiles (the repo's 6 types), description, start / end, Repeat
 * (summary row + Edit → the shared Repeat nested screen; read-only on an
 * existing series), conflicts with loaded appointments, and This event /
 * This and following / All events scopes for series edits and deletes.
 * Existing time-off API only.
 */
export default function TimeBlockDrawer(props: TimeBlockDrawerProps) {
  const { opened, onClose, isEdit } = props;
  const key = isEdit
    ? ((props.initialData as TimeBlockEvent | null)?.id ?? "edit")
    : "new";
  return (
    <CalendarDrawer
      opened={opened}
      // X on the Repeat screen goes back to the form, not out of the drawer.
      onClose={props.nested === "repeat" ? props.onCloseNested : onClose}
      title={isEdit ? "Edit time block" : "New time block"}
    >
      {opened && <TimeBlockBody key={key} {...props} />}
    </CalendarDrawer>
  );
}

/** Hosts the Repeat screen over a sheet (mobile: a full-screen push). */
export type NestedLayer = React.ComponentType<{
  opened: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
}>;

export interface TimeBlockBodyProps extends Omit<
  TimeBlockDrawerProps,
  "opened"
> {
  /** "sheet": inside the mobile bottom sheet (its gutters, 44px targets). */
  variant?: "drawer" | "sheet";
  /** Renders Repeat in this layer instead of replacing the body. */
  nestedLayer?: NestedLayer;
}

/**
 * The time block form: the desktop drawer's body and the mobile time block
 * sheet's (same fields, Repeat, conflicts and series scopes).
 */
export function TimeBlockBody({
  onClose,
  initialData,
  isEdit,
  isLoading,
  onSubmit,
  onDelete,
  timezone,
  viewedDay,
  events,
  nested,
  onOpenNested,
  onCloseNested,
  onDraftChange,
  variant = "drawer",
  nestedLayer: Layer,
}: TimeBlockBodyProps) {
  const { employees } = useSalon() as { employees: any[] };
  const editing = isEdit ? (initialData as TimeBlockEvent) : null;
  const series = isSeries(editing);
  const [form, setForm] = useState<TimeBlockFormState>(() =>
    initialTimeBlockForm(initialData, viewedDay),
  );
  const [errors, setErrors] = useState<TimeBlockErrors>({});
  const [scopeFor, setScopeFor] = useState<"save" | "delete" | null>(null);
  const repeatEditRef = useRef<HTMLButtonElement>(null);
  const repeatErrorId = useId();
  const set = (patch: Partial<TimeBlockFormState>) =>
    setForm((f) => ({ ...f, ...patch }));

  // New block: outline it on the grid as the form changes.
  useEffect(() => {
    if (!isEdit) onDraftChange?.(draftTimeBlockEvent(form));
  }, [form, isEdit, onDraftChange]);
  useEffect(() => () => onDraftChange?.(null), [onDraftChange]);

  const staff = filterBookableActiveStaff(employees || [], form.staffId);
  const staffOptions = staff.map((e: any) => ({
    value: e.id,
    label: `${e.first_name ?? ""} ${e.last_name ?? ""}`.trim(),
  }));
  const photoOf = (id: string): string | undefined => {
    const e = staff.find((x: any) => x.id === id);
    return e?.staff_photo_url ?? e?.avatar_url ?? undefined;
  };
  const conflicts = useMemo(
    () => timeBlockConflicts(form, events, editing?.id),
    [form, events, editing?.id],
  );

  const submit = (appliesTo?: "single" | "hereafter" | "all") => {
    const errs = validateTimeBlock(form, isEdit);
    setErrors(errs);
    // A stale Repeat end is fixed on the Repeat screen: focus its Edit (only
    // when it's the sole error, so focus doesn't skip past earlier fields).
    if (errs.rule && Object.keys(errs).length === 1) {
      repeatEditRef.current?.focus();
    }
    if (Object.keys(errs).length) return;
    if (isEdit && series && !appliesTo) {
      setScopeFor("save");
      return;
    }
    onSubmit(
      buildTimeBlockPayload(form, {
        timezone,
        isEdit,
        id: editing?.id,
        appliesTo,
      }),
    );
  };

  const remove = () => {
    if (series) setScopeFor("delete");
    else onDelete("single");
  };

  const repeatOpen = nested === "repeat" && !isEdit;
  const repeatScreen = repeatOpen ? (
    <RepeatScreen
      startDay={form.startDay}
      endDay={form.endDay}
      rule={form.rule}
      onBack={onCloseNested}
      onDone={(rule) => {
        set({ rule });
        // Done only accepts a valid rule, so its submit error is resolved.
        setErrors(({ rule: _fixed, ...rest }) => rest);
        onCloseNested();
      }}
    />
  ) : null;
  if (repeatOpen && !Layer) return repeatScreen;

  const repeatSummary = editing
    ? seriesText(
        editing.recurrenceInterval,
        editing.recurrenceUnit,
        editing.recurrenceEndDate,
      )
    : ruleText(form.rule, form.startDay);

  return (
    <Box className={styles.body} data-variant={variant}>
      <Stack gap={16} className={styles.scroll}>
        <Select
          label="Staff member"
          placeholder="Select staff"
          data={staffOptions}
          value={form.staffId || null}
          onChange={(v) => set({ staffId: v || "" })}
          error={errors.staffId}
          disabled={isEdit}
          searchable
          comboboxProps={{ withinPortal: false }}
          renderOption={({ option }) => (
            <Group gap={8} wrap="nowrap">
              <Avatar
                src={photoOf(option.value)}
                size={24}
                radius="xl"
                classNames={{ placeholder: styles.avatar }}
              >
                {getInitials(option.label)}
              </Avatar>
              <Text size="sm">{option.label}</Text>
            </Group>
          )}
        />

        <Box>
          <Text className={styles.label}>Type</Text>
          <Box className={styles.tiles} role="radiogroup" aria-label="Type">
            {TIME_BLOCK_TYPES.map((t) => {
              const Icon = timeBlockIcon(t);
              const on = form.type === t;
              return (
                <UnstyledButton
                  key={t}
                  role="radio"
                  aria-checked={on}
                  data-on={on || undefined}
                  className={styles.tile}
                  onClick={() => set({ type: t })}
                >
                  <Icon size={18} />
                  <Text component="span" className={styles.tileLabel}>
                    {t}
                  </Text>
                </UnstyledButton>
              );
            })}
          </Box>
          {errors.type && <Text className={styles.error}>{errors.type}</Text>}
        </Box>

        <Textarea
          label="Description"
          placeholder="Optional"
          autosize
          minRows={2}
          value={form.description}
          onChange={(e) => set({ description: e.currentTarget.value })}
        />

        <Box>
          <Text className={styles.label}>Start</Text>
          <Group gap={8} wrap="nowrap" grow>
            <TextInput
              type="date"
              aria-label="Start date"
              value={form.startDay}
              onChange={(e) => {
                const v = e.currentTarget.value;
                // Same-day blocks keep their end day in step.
                set({
                  startDay: v,
                  endDay: form.endDay === form.startDay ? v : form.endDay,
                });
              }}
            />
            <Select
              aria-label="Start time"
              data={TIME_OPTIONS}
              value={form.startTime}
              allowDeselect={false}
              searchable
              comboboxProps={{ withinPortal: false }}
              onChange={(v) => {
                if (!v) return;
                set({
                  startTime: v,
                  endTime:
                    form.endDay === form.startDay && form.endTime <= v
                      ? addMinutesToTime(v, 60)
                      : form.endTime,
                });
              }}
              error={errors.startTime}
            />
          </Group>
        </Box>
        <Box>
          <Text className={styles.label}>End</Text>
          <Group gap={8} wrap="nowrap" grow>
            <TextInput
              type="date"
              aria-label="End date"
              min={form.startDay}
              value={form.endDay}
              onChange={(e) => set({ endDay: e.currentTarget.value })}
            />
            <Select
              aria-label="End time"
              data={TIME_OPTIONS}
              value={form.endTime}
              allowDeselect={false}
              searchable
              comboboxProps={{ withinPortal: false }}
              onChange={(v) => v && set({ endTime: v })}
              error={errors.endTime}
            />
          </Group>
        </Box>

        <Box>
          <Text className={styles.label}>Repeat</Text>
          <Group
            wrap="nowrap"
            className={styles.repeatRow}
            data-error={errors.rule ? true : undefined}
          >
            <Box component="span" className={styles.repeatIcon}>
              <DESIGN_ICONS.CalendarClock size={16} />
            </Box>
            <Text className={styles.repeatText}>{repeatSummary}</Text>
            {!isEdit && (
              <UnstyledButton
                ref={repeatEditRef}
                className={styles.editLink}
                onClick={() => onOpenNested("repeat")}
                aria-describedby={errors.rule ? repeatErrorId : undefined}
              >
                Edit
              </UnstyledButton>
            )}
          </Group>
          {errors.rule && (
            <Text id={repeatErrorId} className={styles.error}>
              {errors.rule}
            </Text>
          )}
          {series && (
            <Text className={styles.hint}>
              A series' repeat rule can't be changed.
            </Text>
          )}
        </Box>

        {conflicts.length > 0 && (
          <Box className={styles.conflicts} role="status">
            <Group gap={6} wrap="nowrap">
              <IconAlertTriangle size={15} />
              <Text className={styles.conflictTitle}>
                Overlaps {conflicts.length} appointment
                {conflicts.length === 1 ? "" : "s"}
              </Text>
            </Group>
            <Stack gap={2} mt={6}>
              {conflicts.slice(0, 5).map((c) => (
                <Text key={c.id} className={styles.conflictRow}>
                  {c.label}
                </Text>
              ))}
              {conflicts.length > 5 && (
                <Text className={styles.conflictRow}>
                  +{conflicts.length - 5} more
                </Text>
              )}
            </Stack>
          </Box>
        )}
      </Stack>

      <Group gap={8} wrap="nowrap" className={styles.footer}>
        {isEdit && (
          <Button
            variant="subtle"
            color="red"
            onClick={remove}
            disabled={isLoading}
          >
            Delete
          </Button>
        )}
        <Box style={{ flex: 1 }} />
        <Button variant="default" onClick={onClose} disabled={isLoading}>
          Cancel
        </Button>
        <Button color="teal" onClick={() => submit()} loading={isLoading}>
          {isEdit ? "Save" : "Add time block"}
        </Button>
      </Group>

      <Modal
        opened={!!scopeFor}
        onClose={() => setScopeFor(null)}
        title={
          scopeFor === "delete"
            ? "Delete recurring time block"
            : "Edit recurring time block"
        }
        centered
        size="sm"
        zIndex={400}
      >
        <Stack gap={8}>
          {SERIES_SCOPES.map((s) => (
            <Button
              key={s.value}
              variant={s.value === "single" ? "filled" : "default"}
              color={scopeFor === "delete" ? "red" : "teal"}
              onClick={() => {
                const which = scopeFor;
                setScopeFor(null);
                if (which === "delete") onDelete(s.value);
                else submit(s.value);
              }}
            >
              {s.label}
            </Button>
          ))}
          <Button
            variant="subtle"
            color="gray"
            onClick={() => setScopeFor(null)}
          >
            Cancel
          </Button>
        </Stack>
      </Modal>
      {Layer && (
        <Layer opened={repeatOpen} onClose={onCloseNested} title="Repeat">
          {repeatScreen}
        </Layer>
      )}
    </Box>
  );
}
